import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

// Redux
import { useSelector } from "react-redux";

import { formatDate } from "../../components/Reports/Components/utils";

// Helpers / API
import axios from "axios";
import { toast } from "react-toastify";
import { useGlobalContext } from "../../context/global";
import useUserDetails from "../../helper/userData/useUserDetails";
import useInitialLoad from "../../hooks/useInitialLoad";
import useWrapperInsightsQuery from "../../hooks/useWrapperInsightsQuery";
import { mergeCustomOutputToAPIFiltersFormat } from "../../helper/Quadrant/quadrantHelper";
import { processFields } from "../../components/Filter/subComponents/filterHelper";
import {
  getPerformanceAllColumns,
  PerformanceAnalysisGroupByOptions,
  PerformanceAnalysisMeasures,
} from "../PerformanceAnalysis/performanceAnalysisConfigs";
import {
  FiltersCampaignDataPerformance,
  FiltersDimensionDataPerformance,
  FiltersMeasuresDataPerformance,
} from "../PerformanceAnalysis/performanceAnalysisFilterConfig";
import {
  buildPlacementBidsWrapperExportQueryBody,
  exportPlacementBidsReport,
  getPlacementBidsWrapperVisibleColumnKeys,
} from "../../helper/PageWiseExportLogic/placementBidsExport";
import {
  DEFAULT_PLACEMENT_DIMENSIONS,
  PLACEMENT_BIDS_ALWAYS_MEASURES,
  buildPlacementInsightsQueryBody,
  clearAllSelections,
  injectSearchIntoFilters,
  injectSpCampaignTypeFilter,
  isCampaignTypeSP,
  mapPlacementBidColumns,
  getPlacementBidsV2MeasureFilterConfig,
  normalizePlacementInsightRows,
  prepareWrapperFilters,
  resolveDefaultGroupByDimensions,
  SB_SD_OPTIMISATION_MODAL_NOTE,
} from "./adjustBidLocalFilters";
import { getPlacementBidsBidRules } from "./placementBidsV2BidConfig";
import {
  ColumnCustomizationButton,
  COLUMN_CUSTOMIZATION_PAGE_IDS,
  V2_STANDARD_VIEW_OPTIONS,
  usePageColumnCustomization,
} from "../../components/ColumnCustomization";
import {
  QuickFilterButton,
  QUICK_FILTER_PAGE_IDS,
} from "../../components/QuickFilter";

import {
  BULK_SCOPE,
  applyBulkRowToggle,
  applyBulkSelectAllPage,
  applySelectAllMatching,
  clearBulkSelection,
  getRowBulkId,
  getSelectedRowIds,
  shouldShowSelectAllMatchingBanner,
} from "../../helper/bulkSelection/bulkSelectionModes";
import { buildBulkQuery } from "../../helper/bulkSelection/buildBulkQuery";

// Components
import SectionTitle from "../../components/sectionTitle";
import PageVersionSwitchButton from "../../components/Buttons/PageVersionSwitchButton";
import Breadcrumb from "../../components/Breadcrumb/ReportsBreadCrumb";
import BulkActionBarV2 from "../../components/Bars/BulkActionBarV2";
import BulkActionConfirmModalV2 from "../../components/modal/BulkActionConfirmModalV2";
import BulkAdjustBidModalV2 from "../../components/modal/BulkAdjustBidModalV2";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import ExportDownloadTooltip, {
  EXPORT_LIMIT_TOOLTIP_MESSAGE,
  EXPORT_TRACK_TOOLTIP_MESSAGE,
  getExportRedirectIconButtonSx,
} from "../../components/Reports/Components/ExportDownloadTooltip";
import ComplexFilterWidget from "../../components/Filter/ComplexFilterWidget";
import AppliedFiltersChips from "../../components/Filter/AppliedFiltersChips";
import AdjustBidV2Table from "./AdjustBidV2Table";
import {
  buildBulkPlacementBidUpdateBody,
  getRequestedCountForBulk,
  submitBulkPlacementBidUpdate,
} from "../../helper/bulkSelection/bulkPlacementBidUpdate";
import { openRequestTrackingPage } from "../../helper/common/openRequestTrackingPage";
import { useAuthHeader } from "react-auth-kit";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  ArrowDropDown,
  ArrowDropUp,
  FilterAlt,
  FilterAltOutlined,
} from "@mui/icons-material";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 300, 500];
const REPORT_API = process.env.REACT_APP_REPORT_API;
const USER_PLATFORM = "amazon"; // Placement Bids v2 only ever talks to the amazon wrapper endpoints
const EMPTY_TOPX_CONFIG = { operators: {}, fields: [] };
const PLACEMENT_BULK_ID_KEYS = ["campaign_id", "placement_name"];

const AdjustBidV2 = () => {
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const location = useLocation();
  const viewTYPE =
    new URLSearchParams(location.search).get("viewtype") ?? "PERFORMANCE";
  const authHeader = useAuthHeader();
  const token = authHeader();
  const { calendarDate, applyCalendar, clientId } = useGlobalContext();
  const [userDetails] = useUserDetails();
  const [initialLoad, setInitialLoad] = useInitialLoad();

  // ---- ComplexFilterWidget / filter state ----------------------------------
  const [showComplexFilter, setShowComplexFilter] = useState(false);
  const [filterModuleFilters, setFilterModuleFilters] = useState({});
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  // ---- "View more Insights" menu ---------------------------------------
  const [insightsMenuAnchor, setInsightsMenuAnchor] = useState(null);

  const measureFilterConfig = getPlacementBidsV2MeasureFilterConfig(
    FiltersMeasuresDataPerformance[USER_PLATFORM]
  );
  const filterWidgetConfig = {
    campaigns: FiltersCampaignDataPerformance[USER_PLATFORM],
    measure: measureFilterConfig,
    dimension: FiltersDimensionDataPerformance[USER_PLATFORM],
    topXConfig: EMPTY_TOPX_CONFIG,
  };

  useEffect(() => {
    const init = async () => {
      const dimensionConfig = FiltersDimensionDataPerformance[USER_PLATFORM];
      const measureConfig = FiltersMeasuresDataPerformance[USER_PLATFORM];
      const campaignConfig = FiltersCampaignDataPerformance[USER_PLATFORM];
      if (dimensionConfig) {
        dimensionConfig.fields = await processFields(dimensionConfig.fields ?? [], clientId, []);
      }
      if (measureConfig) {
        measureConfig.fields = await processFields(measureConfig.fields ?? [], clientId, []);
      }
      if (campaignConfig) {
        campaignConfig.fields = await processFields(campaignConfig.fields ?? [], clientId, []);
      }
    };
    if (clientId) init();
  }, [clientId]);

  // ---- Analysis by / dimensions / measures / order -------------------------
  const groupByOptionsData = useMemo(
    () => PerformanceAnalysisGroupByOptions[USER_PLATFORM] ?? [],
    []
  );
  const defaultDimensions = useMemo(
    () => resolveDefaultGroupByDimensions(groupByOptionsData, DEFAULT_PLACEMENT_DIMENSIONS),
    [groupByOptionsData]
  );
  const [queryDimensions, setQueryDimensions] = useState(defaultDimensions);
  const [groupByFilter, setGroupByFilter] = useState(() => {
    const match = groupByOptionsData.find((option) =>
      defaultDimensions.every((dimension) => option?.dimensions?.includes(dimension))
    );
    return match?.value ?? groupByOptionsData?.[0]?.value ?? "campaign_placement";
  });
  const queryMeasure = PerformanceAnalysisMeasures[USER_PLATFORM] ?? [];
  const [queryOrder, setQueryOrder] = useState([["spends", "desc"]]);
  const [allColumns, setAllColumns] = useState(
    () => mapPlacementBidColumns(getPerformanceAllColumns(false, defaultDimensions) ?? [])
  );

  useEffect(() => {
    setAllColumns(
      mapPlacementBidColumns(getPerformanceAllColumns(false, queryDimensions) ?? [])
    );
  }, [queryDimensions]);


  const {
    currentViewType,
    selectedMetricKeys,
    columnCollectionId,
    metricColumns,
    columnCollections,
    visibleTableColumns,
    isSaving: isColumnCustomizationSaving,
    syncFromViewType,
    handleApply: handleApplyColumnCustomization,
    handleSave: handleSaveColumnCustomization,
    handleSaveAs: handleSaveAsColumnCustomization,
    handleRenameView: handleRenameColumnView,
    handleDeleteView: handleDeleteColumnView,
  } = usePageColumnCustomization({
    pageId: COLUMN_CUSTOMIZATION_PAGE_IDS.BID_ADJUST_V2,
    allColumns,
    standardViewOptions: V2_STANDARD_VIEW_OPTIONS,
    initialViewType: viewTYPE,
    platform: USER_PLATFORM,
    seedWithAllMetrics: true,
  });

  useEffect(() => {
    const viewtype =
      new URLSearchParams(location.search).get("viewtype") ?? "PERFORMANCE";
    syncFromViewType(viewtype);
  }, [location.search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set("viewtype", currentViewType);
    const newSearch = queryParams.toString();
    const currentSearch = window.location.search.replace(/^\?/, "");
    if (newSearch === currentSearch) return;
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${newSearch}`
    );
  }, [currentViewType]);


  const availableExtraGroupByOptions = useMemo(() => {
    const dimensionColumnCount = allColumns.filter((column) => column?.isDimension).length;
    if (dimensionColumnCount >= 4) return [];
    return (groupByOptionsData || []).filter((option) => {
      if (option.value === groupByFilter) return false;
      if (option?.disabledDimensions?.some((dimension) => queryDimensions.includes(dimension))) {
        return false;
      }
      if (!option?.dimensions?.length) return false;
      const isAlreadyAdded = option.dimensions.every((dimension) =>
        queryDimensions.includes(dimension)
      );
      return !isAlreadyAdded;
    });
  }, [allColumns, groupByFilter, groupByOptionsData, queryDimensions]);

  const handleExtraDimensionChange = (dimensions = [], action = "add") => {
    if (action === "add") {
      if (allColumns.filter((column) => column?.isDimension).length >= 4) return;
      setQueryDimensions((prev) => [...new Set([...(prev || []), ...(dimensions || [])])]);
      setCurrentPage(1);
    } else if (action === "remove") {
      setQueryDimensions((prev) => prev.filter((dimension) => !dimensions.includes(dimension)));
      setCurrentPage(1);
    }
  };

  const [queryTimeDimensions, setQueryTimeDimensions] = useState([
    {
      dimension: "date",
      dateRange: [
        formatDate(calendarDate[0].startDate),
        formatDate(calendarDate[0].endDate),
      ],
    },
  ]);

  useEffect(() => {
    const startDate = formatDate(calendarDate[0].startDate);
    const endDate = formatDate(calendarDate[0].endDate);
    if (
      startDate !== queryTimeDimensions[0].dateRange[0] ||
      endDate !== queryTimeDimensions[0].dateRange[1]
    ) {
      setQueryTimeDimensions((prev) => {
        const data = [...prev];
        data[0] = { ...data[0], dateRange: [startDate, endDate] };
        return data;
      });
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyCalendar]);

  // ---- Pagination / search --------------------------------------------------
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationLimit, setPaginationLimit] = useState(50);
  const [inputValue, setInputValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  // ---- Data ------------------------------------------------------------------
  const {
    rows: dataSource,
    setRows: setDataSource,
    summaryData,
    totalCount,
    isLoading: isLoaderVisible,
    runQuery,
    resetResult,
  } = useWrapperInsightsQuery({
    errorMessage: "Some unknown error occurred while loading data!",
  });
  const [selectedTargeting, setSelectedTargeting] = useState([]);
  const [bulkScope, setBulkScope] = useState(BULK_SCOPE.SELECTED);
  const [bulkExcludeIds, setBulkExcludeIds] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [isBulkAdjustModalOpen, setIsBulkAdjustModalOpen] = useState(false);
  const [pendingBulkOperation, setPendingBulkOperation] = useState(null);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [isBulkSuccess, setIsBulkSuccess] = useState(false);

  // ---- Export ------------------------------------------------------------------
  const [isExportDownloading, setIsExportDownloading] = useState(false);
  const [isExportRedirectMode, setIsExportRedirectMode] = useState(false);

  useEffect(() => {
    setSelectedTargeting(dataSource?.filter((row) => row?.isChecked) ?? []);
  }, [dataSource]);

  const selectedBulkIds = useMemo(
    () => getSelectedRowIds(dataSource, PLACEMENT_BULK_ID_KEYS),
    [dataSource]
  );
  const pageSelectableCount = useMemo(
    () => dataSource.filter((row) => isCampaignTypeSP(row?.campaign_type)).length,
    [dataSource]
  );
  const showSelectAllMatching = shouldShowSelectAllMatchingBanner({
    scope: bulkScope,
    pageSelectedCount: selectedTargeting.length,
    pageSelectableCount,
    totalCount,
  });

  useEffect(() => {
    setBulkScope(BULK_SCOPE.SELECTED);
    setBulkExcludeIds([]);
    setDataSource((prev) => clearAllSelections(prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    paginationLimit,
    filterModuleFilters,
    searchValue,
    queryOrder,
    queryDimensions,
    queryTimeDimensions,
  ]);

  useEffect(() => {
    setIsExportRedirectMode(false);
    setIsBulkSuccess(false);
  }, [filterModuleFilters, queryTimeDimensions, queryOrder, queryDimensions, searchValue]);

  // ---- Search dimensions ----------------------------------------------------
  // Search across every active group-by dimension (includes campaign_name even
  // when it is only nested under the campaign_id column UI).
  function getSelectedSearchDimensions() {
    return [...new Set(queryDimensions || [])];
  }

  function getPlaceholderText() {
    const labels = allColumns
      .filter((column) => column?.isDimension)
      .map((column) => column.shortTitle)
      .filter(Boolean);
    return `Search ${[...new Set(labels)].join(", ")}`;
  }

  // ---- Core fetch -------------------------------------------------------------
  function handleDataFetch(moduleFilters) {
    if (!clientId) return;

    const mergedFilters = mergeCustomOutputToAPIFiltersFormat(
      moduleFilters?.metrics?.custom_output,
      moduleFilters?.dimension?.custom_output,
      moduleFilters?.campaigns?.custom_output,
      moduleFilters?.topX?.custom_output
    );

    const { filters: transformedFilters, isEmptyMatch } =
      prepareWrapperFilters(mergedFilters);

    if (isEmptyMatch) {
      resetResult();
      setInitialLoad(false);
      return;
    }

    const filtersWithSearch = injectSearchIntoFilters(
      transformedFilters,
      searchValue,
      getSelectedSearchDimensions()
    );

    runQuery(async ({ signal }) => {
      const queryBody = buildPlacementInsightsQueryBody({
        queryDimensions,
        filters: filtersWithSearch,
        campaignsFilter: undefined,
        queryMeasure,
        queryOrder,
        dateRange: queryTimeDimensions[0]?.dateRange ?? [],
        offset: (currentPage - 1) * paginationLimit,
        limit: paginationLimit,
      });

      // TODO: remove before production — summary currently omits group-by dimensions from measures
      // (insights still sends measures + dimensions). Align both once backend is ready.
      const summaryQueryBody = {
        query: {
          ...queryBody.query,
          measures: [
            ...new Set([...(queryMeasure || []), ...PLACEMENT_BIDS_ALWAYS_MEASURES]),
          ],
        },
      };

      const insightsUrl = `${REPORT_API}/client/${clientId}/placement:insights?source=amazon`;
      const summaryUrl = `${REPORT_API}/client/${clientId}/placement:insights-summary?source=amazon`;

      const [summaryRes, dataRes] = await Promise.all([
        axios.post(summaryUrl, summaryQueryBody, { signal }),
        axios.post(insightsUrl, queryBody, { signal }),
      ]);

      return {
        rows: normalizePlacementInsightRows(dataRes?.data?.data ?? []),
        summaryData: summaryRes?.data ?? {},
        totalCount: summaryRes?.data?.total_count ?? 0,
      };
    }).finally(() => {
      setInitialLoad(false);
    });
  }

  useEffect(() => {
    handleDataFetch(filterModuleFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterModuleFilters,
    queryDimensions,
    queryTimeDimensions,
    queryOrder,
    currentPage,
    paginationLimit,
    searchValue,
    searchTrigger,
    clientId,
  ]);

  // ---- Filter widget handlers ----------------------------------------------
  const handleFilterOutput = (filters) => {
    setCurrentPage(1);
    setShowComplexFilter(false);
    const hasFilters =
      (filters?.dimension?.data?.length ?? 0) > 0 || (filters?.metrics?.data?.length ?? 0) > 0;
    setIsFilterApplied(hasFilters);
    setFilterModuleFilters(filters);
  };

  const FilterComponent = (
    <ComplexFilterWidget
      key={`placement_bids_v2_${showComplexFilter}`}
      config={filterWidgetConfig}
      campaignFilter={false}
      showDimensionFilter={true}
      filters={filterModuleFilters}
      showPopupModal={showComplexFilter}
      setShowPopupModal={setShowComplexFilter}
      output={handleFilterOutput}
      prefixMember={""}
      type="page"
      sidebar={true}
      default={"metrics"}
      page={"overlay"}
      isAppliedCallback={() => {}}
      summary={"always"}
      summaryOutput={() => {}}
      calloutTitle="Select your filters"
      calloutContent="+ Add Filter"
      hideFilterSummary={true}
    />
  );

  // ---- Pagination handlers -------------------------------------------------
  const totalPages = Math.max(1, Math.ceil(totalCount / paginationLimit));

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePageLimitChange = (event) => {
    setPaginationLimit(Number(event.target.value));
    setCurrentPage(1);
  };

  // ---- Selection handlers ---------------------------------------------------
  const singleCheckBoxHandler = (index) => {
    setDataSource((prev) => {
      const result = applyBulkRowToggle({
        rows: prev,
        index,
        scope: bulkScope,
        excludeIds: bulkExcludeIds,
        idKeys: PLACEMENT_BULK_ID_KEYS,
        isRowSelectable: (row) => isCampaignTypeSP(row?.campaign_type),
      });
      setBulkScope(result.scope);
      setBulkExcludeIds(result.excludeIds);
      return result.rows;
    });
  };

  const allCheckBoxHandler = (checked) => {
    setDataSource((prev) => {
      const result = applyBulkSelectAllPage({
        rows: prev,
        checked,
        isRowSelectable: (row) => isCampaignTypeSP(row?.campaign_type),
      });
      setBulkScope(result.scope);
      setBulkExcludeIds(result.excludeIds);
      return result.rows;
    });
  };

  const removeAllSelectedTargetings = () => {
    setDataSource((prev) => clearBulkSelection(prev).rows);
    setBulkScope(BULK_SCOPE.SELECTED);
    setBulkExcludeIds([]);
  };

  const handleSelectAllMatching = () => {
    setDataSource((prev) => {
      const result = applySelectAllMatching({
        rows: prev,
        isRowSelectable: (row) => isCampaignTypeSP(row?.campaign_type),
      });
      setBulkScope(result.scope);
      setBulkExcludeIds(result.excludeIds);
      return result.rows;
    });
  };

  const prepareBulkQuery = () => {
    const mergedFilters = mergeCustomOutputToAPIFiltersFormat(
      filterModuleFilters?.metrics?.custom_output,
      filterModuleFilters?.dimension?.custom_output,
      filterModuleFilters?.campaigns?.custom_output,
      filterModuleFilters?.topX?.custom_output
    );
    const { filters: transformedFilters } = prepareWrapperFilters(mergedFilters);
    let filtersWithSearch = injectSearchIntoFilters(
      transformedFilters,
      searchValue,
      getSelectedSearchDimensions()
    );
    if (
      bulkScope === BULK_SCOPE.ALL_MATCHING ||
      bulkScope === BULK_SCOPE.ALL_MATCHING_EXCEPT
    ) {
      filtersWithSearch = injectSpCampaignTypeFilter(filtersWithSearch);
    }
    const excludeRows = dataSource.filter((row) =>
      bulkExcludeIds.includes(getRowBulkId(row, PLACEMENT_BULK_ID_KEYS))
    );
    const measuresForBulk = [
      ...new Set([...(queryMeasure || []).filter(Boolean), "spends"]),
    ];
    return buildBulkQuery({
      scope: bulkScope,
      baseFilters: filtersWithSearch,
      selectedRows: selectedTargeting,
      excludeRows,
      idKeys: PLACEMENT_BULK_ID_KEYS,
      queryContext: {
        dimensions: queryDimensions,
        measures: measuresForBulk,
        order: queryOrder,
        timeDimensions: queryTimeDimensions,
      },
    });
  };

  // ---- Bulk bid edit: Adjust Bid → modal → confirm → API → track ----------
  const editBidHandler = () => {
    setPendingBulkOperation(null);
    setIsBulkSuccess(false);
    setIsBulkAdjustModalOpen(true);
  };

  const handleBulkAdjustModalSubmit = (operation) => {
    setPendingBulkOperation(operation);
    const isPercentage = operation?.adjustmentType?.includes("PERCENTAGE");
    const valueText = isPercentage
      ? `${operation.value}%`
      : String(operation.value);
    const hasFallbackBid =
      isPercentage &&
      operation.defaultBaseValue !== null &&
      operation.defaultBaseValue !== undefined &&
      operation.defaultBaseValue !== "";
    setBulkConfirm({
      actionKey: "Adjust Bid",
      title: "Confirm bulk bid adjustment?",
      actionLabel: "Confirm",
      description: (
        <>
          Apply <Box component="span" sx={{ fontWeight: 700 }}>{operation.label}</Box>
          {" "}
          (<Box component="span" sx={{ fontWeight: 700 }}>{valueText}</Box>)
          {" "}to the selected placements.
          {isPercentage ? (
            hasFallbackBid ? (
              <>
                {" "}Placements with no bid will be set to{" "}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {operation.defaultBaseValue}%
                </Box>
                {" "}(the percentage is not applied).
              </>
            ) : (
              " Placements with no bid will be skipped and left unchanged."
            )
          ) : null}
        </>
      ),
    });
  };

  const handleBulkConfirm = async () => {
    if (!bulkConfirm || !pendingBulkOperation || isBulkSubmitting) {
      setBulkConfirm(null);
      return;
    }

    const requestedCount = getRequestedCountForBulk({
      scope: bulkScope,
      selectedIdsCount: selectedBulkIds.length,
      totalCount,
      excludeIdsCount: bulkExcludeIds.length,
    });

    if (requestedCount <= 0) {
      toast.info("No placements selected for bulk update.");
      setBulkConfirm(null);
      setPendingBulkOperation(null);
      return;
    }

    const bulkPayload = prepareBulkQuery();
    const email =
      userDetails?.user?.email ?? localStorage.getItem("email") ?? "";
    const body = buildBulkPlacementBidUpdateBody({
      query: bulkPayload?.query,
      adjustmentType: pendingBulkOperation.adjustmentType,
      value: pendingBulkOperation.value,
      email,
      requestedCount,
      defaultBaseValue: pendingBulkOperation.defaultBaseValue,
    });

    setBulkConfirm(null);
    setIsBulkAdjustModalOpen(false);
    setIsBulkSubmitting(true);
    setInitialLoad(true);

    try {
      await submitBulkPlacementBidUpdate({
        clientId,
        userPlatform: USER_PLATFORM,
        skipEvents: true,
        body,
        token,
      });
      setPendingBulkOperation(null);
      removeAllSelectedTargetings();
      setIsBulkSuccess(true);
      setBulkConfirm({
        actionKey: "Adjust Bid",
        title: "Request Submitted",
        actionLabel: "Close",
        description: null,
      });
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to submit bulk bid update."
      );
      setBulkConfirm(null);
    } finally {
      setIsBulkSubmitting(false);
      setInitialLoad(false);
    }
  };

  // ---- Export ---------------------------------------------------------------
  const handlePlacementExportClick = async () => {
    if (isExportRedirectMode) {
      openRequestTrackingPage();
      return;
    }

    const exportTotalCount = totalCount || dataSource?.length || 0;
    if (exportTotalCount <= 0) {
      toast.info("No records available for export.");
      return;
    }

    const mergedFilters = mergeCustomOutputToAPIFiltersFormat(
      filterModuleFilters?.metrics?.custom_output,
      filterModuleFilters?.dimension?.custom_output,
      filterModuleFilters?.campaigns?.custom_output,
      filterModuleFilters?.topX?.custom_output
    );
    const { filters: transformedFilters, isEmptyMatch } = prepareWrapperFilters(mergedFilters);

    if (isEmptyMatch) {
      toast.info("No records available for export.");
      return;
    }

    const filtersWithSearch = injectSearchIntoFilters(
      transformedFilters,
      searchValue,
      getSelectedSearchDimensions()
    );

    const insightsQueryBody = buildPlacementInsightsQueryBody({
      queryDimensions,
      filters: filtersWithSearch,
      campaignsFilter: undefined,
      queryMeasure,
      queryOrder,
      dateRange: queryTimeDimensions[0]?.dateRange ?? [],
    });
    // Exports run as an async backend job over the full result set - strip pagination.
    delete insightsQueryBody.query.offset;
    delete insightsQueryBody.query.limit;

    const queryBody = buildPlacementBidsWrapperExportQueryBody({
      query: insightsQueryBody.query,
    });

    await exportPlacementBidsReport({
      clientId,
      userPlatform: USER_PLATFORM,
      triggeredBy: userDetails?.user?.email ?? localStorage.getItem("email") ?? "",
      totalCount: exportTotalCount,
      queryBody,
      visibleColumnKeys: getPlacementBidsWrapperVisibleColumnKeys(visibleTableColumns),
      setIsExporting: setIsExportDownloading,
      onExportSuccess: () => setIsExportRedirectMode(true),
    });
  };

  const showGroupByPlusIcon =
    availableExtraGroupByOptions.length > 0 &&
    allColumns.filter((column) => column?.isDimension).length < 4;

  const placementBidRules = useMemo(() => getPlacementBidsBidRules(), []);

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box-master-ads"
    >
      <div className="campaign-list-container overflow-hidden">
        <BulkAdjustBidModalV2
          open={isBulkAdjustModalOpen}
          previewRows={bulkScope === BULK_SCOPE.SELECTED ? selectedTargeting : []}
          bidRules={placementBidRules}
          infoNote={SB_SD_OPTIMISATION_MODAL_NOTE}
          enableMissingBidFallback
          onClose={() => {
            setIsBulkAdjustModalOpen(false);
            setPendingBulkOperation(null);
          }}
          onSubmit={handleBulkAdjustModalSubmit}
        />
        <Box>
          <Breadcrumb />
          <Stack direction="row" alignItems="center">
            <SectionTitle sectionTitleHeading={"Placement Adjustment Bid Optimizer"} />
            <PageVersionSwitchButton pageKey="bid-adjust" isNewVersion />
          </Stack>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
              mt: 1,
              position: "relative",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Autocomplete
              options={groupByOptionsData}
              disableClearable
              disabled
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Analysis by"
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: "14px",
                      color: "#333333",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#333333",
                    },
                    "& .MuiInputBase-root": {
                      height: "36px",
                      fontSize: "12px !important",
                      color: "#333333",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "12px !important",
                      color: "#333333",
                      padding: "6px 8px",
                    },
                  }}
                />
              )}
              value={groupByOptionsData?.find((o) => o.value === groupByFilter) || null}
              onChange={(event, selectedOption) => {
                if (selectedOption) {
                  setQueryDimensions([...selectedOption.dimensions]);
                  setGroupByFilter(selectedOption.value);
                  setCurrentPage(1);
                }
              }}
              size="small"
              sx={{
                minWidth: "200px",
                "& .MuiAutocomplete-inputRoot": {
                  fontSize: "12px",
                  color: "#333333",
                },
                "& .MuiAutocomplete-input": {
                  fontSize: "12px",
                  color: "#333333",
                },
                "& .MuiSvgIcon-root": {
                  color: "#333333",
                },
              }}
              slotProps={{
                listbox: {
                  sx: {
                    maxHeight: 250,
                    overflow: "auto",
                    fontSize: "12px",
                    "& .MuiAutocomplete-option": {
                      fontSize: "12px",
                      color: "#333333",
                      padding: "6px 12px",
                      "&:hover": { backgroundColor: "#EDEDED" },
                    },
                  },
                },
              }}
            />

            <TextField
              size="small"
              placeholder={getPlaceholderText()}
              variant="outlined"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(1);
                  setSearchValue(e.target.value);
                  setSearchTrigger((prev) => prev + 1);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: "16px", color: "#333333" }} />
                  </InputAdornment>
                ),
                endAdornment: searchValue && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchValue("");
                        setInputValue("");
                        setCurrentPage(1);
                      }}
                      edge="end"
                    >
                      <CloseIcon sx={{ fontSize: "16px", color: "#333333" }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                flexGrow: 1,
                minWidth: 200,
                "& .MuiInputBase-root": {
                  height: "36px",
                  fontSize: "12px",
                  color: "#333333",
                },
                "& .MuiInputBase-input": {
                  fontSize: "12px",
                  color: "#333333",
                  padding: "6px 4px",
                },
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "12px",
                  color: "#000000",
                  opacity: 0.6,
                },
              }}
            />


            <ColumnCustomizationButton
              collections={columnCollections}
              columns={metricColumns}
              activeCollectionId={columnCollectionId}
              selectedColumnKeys={selectedMetricKeys}
              onApply={handleApplyColumnCustomization}
              onSave={handleSaveColumnCustomization}
              onSaveAs={handleSaveAsColumnCustomization}
              onRenameView={handleRenameColumnView}
              onDeleteView={handleDeleteColumnView}
              saving={isColumnCustomizationSaving}
            />

            <QuickFilterButton
              clientId={clientId}
              platform={USER_PLATFORM}
              pageId={QUICK_FILTER_PAGE_IDS.BID_ADJUST_V2}
              filterConfig={filterWidgetConfig}
              campaignFilter={false}
              onApply={handleFilterOutput}
            />

            <ToggleButton
              value="filters"
              selected={showComplexFilter}
              onChange={() => setShowComplexFilter(!showComplexFilter)}
              sx={{
                height: "36px",
                borderRadius: 1,
                px: 1.5,
                borderColor: showComplexFilter ? "grey.700" : "grey.400",
                backgroundColor: showComplexFilter ? "grey.100" : "white",
                color: "#333333",
                fontSize: "12px",
                fontWeight: 500,
                textTransform: "none",
                "&:hover": {
                  borderColor: "grey.700",
                  backgroundColor: showComplexFilter ? "grey.200" : "grey.50",
                },
                "& .MuiTypography-root": {
                  fontSize: "12px",
                  color: "#333333",
                },
                "& svg": {
                  color: "#333333",
                },
              }}
            >
              <Badge
                overlap="circular"
                variant="dot"
                invisible={!isFilterApplied}
                sx={{ "& .MuiBadge-badge": { bgcolor: isFilterApplied ? "primary.main" : "transparent" } }}
              >
                {showComplexFilter ? <FilterAlt fontSize="small" /> : <FilterAltOutlined fontSize="small" />}
              </Badge>
              <Typography fontWeight={500} sx={{ ml: 1 }}>
                Filters
              </Typography>
              {showComplexFilter ? <ArrowDropUp fontSize="small" /> : <ArrowDropDown fontSize="small" />}
            </ToggleButton>

            <ExportDownloadTooltip
              autoOpen={isExportRedirectMode}
              title={
                isExportRedirectMode
                  ? EXPORT_TRACK_TOOLTIP_MESSAGE
                  : EXPORT_LIMIT_TOOLTIP_MESSAGE
              }
            >
              <span>
                <IconButton
                  onClick={handlePlacementExportClick}
                  disabled={isExportDownloading}
                  sx={getExportRedirectIconButtonSx(isExportRedirectMode)}
                >
                  {isExportDownloading ? (
                    <CircularProgress size={18} thickness={5} sx={{ color: "grey.600" }} />
                  ) : isExportRedirectMode ? (
                    <OpenInNew sx={{ fontSize: 20 }} />
                  ) : (
                    <FileDownloadOutlined sx={{ fontSize: 22 }} />
                  )}
                </IconButton>
              </span>
            </ExportDownloadTooltip>

            <Button
              variant="outlined"
              size="small"
              startIcon={<InsightsOutlined fontSize="small" />}
              onClick={(event) => setInsightsMenuAnchor(event.currentTarget)}
              sx={{
                height: "36px",
                textTransform: "none",
                fontSize: "12px",
                color: "#333333",
                borderColor: "grey.400",
                whiteSpace: "nowrap",
                "&:hover": {
                  borderColor: "grey.700",
                  backgroundColor: "grey.50",
                },
              }}
            >
              View more Insights
            </Button>
            <Menu
              anchorEl={insightsMenuAnchor}
              open={Boolean(insightsMenuAnchor)}
              onClose={() => setInsightsMenuAnchor(null)}
              disableScrollLock
            >
              <MenuItem
                sx={{ fontSize: "12px" }}
                onClick={() => {
                  window.open("/placement-format-report", "_blank", "noopener,noreferrer");
                  setInsightsMenuAnchor(null);
                }}
              >
                Placement Format Report
              </MenuItem>
            </Menu>
          </Box>

          {showComplexFilter && FilterComponent}
          <AppliedFiltersChips
            filters={filterModuleFilters}
            config={filterWidgetConfig}
            onChange={handleFilterOutput}
            hideSaveActions
          />

          <BulkActionBarV2
            open={
              selectedTargeting.length > 0 ||
              bulkScope === BULK_SCOPE.ALL_MATCHING ||
              bulkScope === BULK_SCOPE.ALL_MATCHING_EXCEPT
            }
            scope={bulkScope}
            selectedIdsCount={selectedBulkIds.length}
            totalCount={totalCount}
            excludeIdsCount={bulkExcludeIds.length}
            showSelectAllMatching={showSelectAllMatching}
            onSelectAllMatching={handleSelectAllMatching}
            onClear={removeAllSelectedTargetings}
            actions={[
              {
                key: "Adjust Bid",
                label: isBulkSubmitting ? "Submitting..." : "Adjust Bid",
                variant: "contained",
                color: "primary",
              },
            ]}
            onAction={() => {
              if (isBulkSubmitting) return;
              editBidHandler();
            }}
          />

          <BulkActionConfirmModalV2
            open={Boolean(bulkConfirm)}
            title={bulkConfirm?.title}
            actionLabel={isBulkSubmitting ? "Submitting..." : bulkConfirm?.actionLabel || "Confirm"}
            description={bulkConfirm?.description}
            infoNote={SB_SD_OPTIMISATION_MODAL_NOTE}
            scope={bulkScope}
            selectedIdsCount={selectedBulkIds.length}
            totalCount={totalCount}
            excludeIdsCount={bulkExcludeIds.length}
            zIndex={1500}
            isSuccess={isBulkSuccess}
            successTitle="Request Submitted"
            successMessage="Your bulk bid update request has been submitted successfully. You can track it on the Request Tracking page."
            onGoToTracking={openRequestTrackingPage}
            onCancel={() => {
              if (isBulkSubmitting) return;
              setBulkConfirm(null);
              setPendingBulkOperation(null);
              setIsBulkSuccess(false);
            }}
            onConfirm={handleBulkConfirm}
          />

          <Typography variant="caption" sx={{ color: "#333333", display: "block", mt: 0.5 }}>
            SB/SD optimisation not supported*
          </Typography>

          <AdjustBidV2Table
            columns={visibleTableColumns}
            rows={dataSource}
            isLoading={isLoaderVisible || initialLoad}
            queryOrder={queryOrder}
            summaryData={summaryData}
            onSortClick={(key, order) => {
              setCurrentPage(1);
              setQueryOrder([[key, order]]);
            }}
            groupByOptionsData={groupByOptionsData}
            extraGroupByOptions={availableExtraGroupByOptions}
            onExtraGroupByChange={handleExtraDimensionChange}
            showGroupByPlusIcon={false}
            onToggleRow={singleCheckBoxHandler}
            onToggleAll={allCheckBoxHandler}
          />

          {!isLoaderVisible && !initialLoad && (totalCount > 0 || dataSource?.length > 0) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                py: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#4f5366" }}>
                <Typography variant="body2" sx={{ fontSize: "12px" }}>
                  Page Size
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={paginationLimit}
                    onChange={handlePageLimitChange}
                    disabled={isLoaderVisible}
                    sx={{ height: 36, fontSize: "12px", color: "#333333" }}
                    MenuProps={{ disableScrollLock: true }}
                  >
                    {PAGE_SIZE_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option} sx={{ fontSize: "12px" }}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                lazyLoading={isLoaderVisible}
                totalItems={totalCount}
                itemsPerPage={paginationLimit}
              />
            </Box>
          )}
        </Box>
      </div>
    </div>
  );
};

export default AdjustBidV2;
